import { z } from "zod";

export const positiveIntegerSchema = z.number().int().positive();
export const stringArraySchema = z.array(z.string());

export const SHEET_FILTER_KEY = "sheet:key";
export const DRAWER_FILTER_KEY = "drawer:key";

export type TSheetDataFilterKey = `sheet:data:${string}`;
export type TSheetFilter = {
  data: Record<string, string>;
};
export type TSheetFilters = Record<string, TSheetFilter>;
export type TDrawerDataFilterKey = `drawer:data:${string}`;
export type TDrawerFilter = {
  data: Record<string, string>;
};
export type TDrawerFilters = Record<string, TDrawerFilter>;

export const isSheetDataFilterKey = (
  value: string,
): value is TSheetDataFilterKey => /^sheet:data:[^:]+$/u.test(value);

export const getSheetDataFilterKey = <TKey extends string>(
  key: TKey,
): `sheet:data:${TKey}` => `sheet:data:${key}`;

export const getSheetDataKey = <TKey extends string>(
  filter: `sheet:data:${TKey}`,
): TKey => filter.slice("sheet:data:".length) as TKey;

export const sheetFiltersSchema: z.ZodType<TSheetFilters> = z.record(
  z.string().min(1),
  z.object({
    data: z.record(z.string().min(1), z.string().min(1)),
  }),
);

export const isDrawerDataFilterKey = (
  value: string,
): value is TDrawerDataFilterKey => /^drawer:data:[^:]+$/u.test(value);

export const getDrawerDataFilterKey = <TKey extends string>(
  key: TKey,
): `drawer:data:${TKey}` => `drawer:data:${key}`;

export const getDrawerDataKey = <TKey extends string>(
  filter: `drawer:data:${TKey}`,
): TKey => filter.slice("drawer:data:".length) as TKey;

export const drawerFiltersSchema: z.ZodType<TDrawerFilters> = z.record(
  z.string().min(1),
  z.object({
    data: z.record(z.string().min(1), z.string().min(1)),
  }),
);

export type TTabFilterKey = `tabs:${string}:key`;
export type TTabFilters = Partial<Record<TTabFilterKey, string>>;

export const isTabFilterKey = (value: string): value is TTabFilterKey => (
  /^tabs:[^:]+:key$/u.test(value)
);

export const getTabFilterKey = <TId extends string>(
  id: TId,
): `tabs:${TId}:key` => `tabs:${id}:key`;

const tabFilterKeySchema = z.custom<TTabFilterKey>((value) => (
  typeof value === "string" && isTabFilterKey(value)
));

export const tabFiltersSchema: z.ZodType<TTabFilters> = z.record(
  tabFilterKeySchema,
  z.string().min(1),
);

export const articleFiltersSchema = z.object({
  "articles:page": positiveIntegerSchema,
  "articles:categories": stringArraySchema,
});

export const filtersSchema = z.object({
  articles: articleFiltersSchema,
  sheets: sheetFiltersSchema,
  drawers: drawerFiltersSchema,
  tabs: tabFiltersSchema,
});

export type TArticleFilters = z.infer<typeof articleFiltersSchema>;
export type TFilters = z.infer<typeof filtersSchema>;
